import type { SourcedRollModifier } from "@/lib/kernel/entity-hydration/roll-modifier/types";

type AnySource = Record<string, unknown>;

export type RollContributors = {
  readonly total: number;
  readonly sourceParts: readonly string[];
  readonly diceParts: readonly string[];
  readonly advLabel: "adv" | "dis" | null;
};

// ---------------------------------------------------------------------------
// Source label helper — maps a SourcedRollModifier source to a short label.
// Handles both production shapes (contributor:"derived", kind:"ability-mod"|
// "proficiency") and the test-fixture shape (kind:"ability"|"proficiency").
// Ported verbatim from lib/rules/dnd5e/character/actor-section.ts lines 45–83.
//
// NOTE: production ability-mod sources (from ability-modifier-projection.ts)
// carry NO `ability` field — the type's `derived` variant has no such property.
// Therefore the modifier renders the generic "ability" label, not "STR"/"WIS"
// etc. The per-ability label (e.g. "STR") would require the kernel source to
// carry the ability name — a follow-up. The row heading (e.g. "STR save") is
// already labelled, so "ability" is an acceptable interim display.
// ---------------------------------------------------------------------------

const sourceLabel = (source: AnySource): string => {
  const kind = source.kind;
  const contributor = source.contributor;

  // Test-fixture shape: { kind: "ability", ability: "str" }
  if (kind === "ability") {
    const ability = source.ability;
    return typeof ability === "string" ? ability.toUpperCase() : "ability";
  }
  // Test-fixture shape: { kind: "proficiency" }
  if (kind === "proficiency") {
    return "prof";
  }
  // Production shape: { contributor: "derived", kind: "ability-mod" }
  if (contributor === "derived" && kind === "ability-mod") {
    const ability = source.ability;
    return typeof ability === "string" ? ability.toUpperCase() : "ability";
  }
  // Production shape: { contributor: "derived", kind: "proficiency" }
  if (contributor === "derived" && kind === "proficiency") {
    return "prof";
  }
  // Relation/profile contributors — use id or kind as fallback
  const relationId = source.relationId;
  if (typeof relationId === "string") {
    return relationId;
  }
  const profileId = source.profileId;
  if (typeof profileId === "string") {
    return profileId;
  }
  if (typeof kind === "string") {
    return kind;
  }
  if (typeof contributor === "string") {
    return contributor;
  }
  return "?";
};

const signed = (n: number): string => (n >= 0 ? `+${n}` : `${n}`);

export const formatRollContributors = (
  mods: readonly SourcedRollModifier[]
): RollContributors => {
  const total = mods.reduce((acc, m) => acc + (m.modifier ?? 0), 0);
  const sourceParts = mods
    .filter((m) => m.modifier !== undefined && m.modifier !== 0)
    .map(
      (m) =>
        `${signed(m.modifier as number)} ${sourceLabel(m.source as AnySource)}`
    );
  const diceParts = mods
    .filter((m) => m.count !== undefined && m.sides !== undefined)
    .map(
      (m) =>
        `${m.subtract ? "-" : "+"}${m.count}d${m.sides} ${sourceLabel(m.source as AnySource)}`
    );
  const hasAdv = mods.some((m) => m.keepHighest);
  const hasDis = mods.some((m) => m.keepLowest);
  const advLabel: "adv" | "dis" | null =
    hasAdv && !hasDis ? "adv" : hasDis && !hasAdv ? "dis" : null;
  return { total, sourceParts, diceParts, advLabel };
};
